import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { formatDateTime } from '../../../shared/tenantClock';
import { useErrorToast } from '../../../shared/ErrorToastContext';
import { NODE_STATUS, SEVERITIES, StatusPill as Pill, RED, GREY } from '../../../shared/PlatformUpdateStatus';

// Platform > Platform Update Check (issue #439): whether the software the
// API runs on has known security issues with fixes available — the Node.js
// version against nodejs.org's release list, and npm audit's findings for
// the API's packages. Shared by every tenant, since they share the runtime.
// Both are checked daily in the background (platformUpdateService.js);
// Check Now runs them straight away. New security findings raise a bell
// alert for every Admin.

const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };
const sectionStyle = { marginTop: 14, padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', maxWidth: 760 };
const LABEL = { fontSize: 13, color: '#5a6b85', width: 150, flexShrink: 0 };
const ROW = { display: 'flex', gap: 12, padding: '4px 0', fontSize: 14 };
const MUTED = { color: '#8a94a6', fontSize: 13 };
const TH = { padding: '8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left', fontSize: 13 };
const TD = { padding: '6px 8px', fontSize: 14, verticalAlign: 'top', borderBottom: '1px solid #eceef1' };

function Field({ label, children }) {
  return (
    <div style={ROW}>
      <div style={LABEL}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

// "Check failed" with why, when the latest attempt didn't succeed.
function FailedBanner({ check, what }) {
  if (!check.error) return null;
  return (
    <div style={{ ...RED, borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>
      The check of {what} on {formatDateTime(check.checked_at)} failed: {check.error.replace(/[.!?]?$/, '.')}
      {check.succeeded_at && <> Showing the last successful check, from {formatDateTime(check.succeeded_at)}.</>}
    </div>
  );
}

function NodeSection({ node }) {
  const status = node.status && NODE_STATUS[node.status];
  return (
    <div style={{ ...sectionStyle, marginTop: 0 }}>
      <h4 style={sectionHeaderStyle}>Node.js</h4>
      <FailedBanner check={node} what="the Node.js release list" />
      <Field label="Running">{node.running}</Field>
      {status ? (
        <>
          <Field label={`Latest ${node.running.split('.')[0]}.x`}>{node.latest}</Field>
          <Field label="Status"><Pill style={status.style}>{status.label}</Pill></Field>
          {node.security_releases?.length > 0 && (
            <Field label="Security releases">{node.security_releases.join(', ')}</Field>
          )}
          <Field label="Checked">{formatDateTime(node.succeeded_at)}</Field>
        </>
      ) : (
        <Field label="Status">
          <span style={MUTED}>{node.checking ? 'Checking…' : 'Not checked yet for this Node.js version.'}</span>
        </Field>
      )}
    </div>
  );
}

function PackagesSection({ packages }) {
  return (
    <div style={sectionStyle}>
      <h4 style={sectionHeaderStyle}>API packages</h4>
      <FailedBanner check={packages} what="the API's packages" />
      {!packages.current ? (
        <div style={MUTED}>
          {packages.checking
            ? 'Checking…'
            : 'No vulnerability results yet for the version of the API that is running.'}
        </div>
      ) : (
        <>
          <Field label="Checked">{formatDateTime(packages.succeeded_at)}</Field>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0' }}>
            {SEVERITIES.map((s) => (
              <div key={s.key} style={{ minWidth: 110, padding: '8px 12px', borderRadius: 8, ...(packages.counts[s.key] ? s.style : GREY) }}>
                <div style={{ fontSize: 22, fontWeight: 600 }}>{packages.counts[s.key]}</div>
                <div style={{ fontSize: 12 }}>{s.label}</div>
              </div>
            ))}
          </div>
          {packages.packages.length === 0 ? (
            <div style={MUTED}>No known vulnerabilities.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
              <thead>
                <tr><th style={TH}>Package</th><th style={TH}>Severity</th><th style={TH}>Fix available</th></tr>
              </thead>
              <tbody>
                {packages.packages.map((p) => {
                  const severity = SEVERITIES.find((s) => s.key === p.severity);
                  return (
                    <tr key={p.name}>
                      <td style={TD}>
                        {p.name}
                        {p.advisories.map((a) => (
                          <div key={a.id} style={{ fontSize: 12 }}>
                            {a.url ? <a href={a.url} target="_blank" rel="noopener noreferrer">{a.title}</a> : a.title}
                          </div>
                        ))}
                      </td>
                      <td style={TD}><Pill style={severity.style}>{severity.label}</Pill></td>
                      <td style={TD}>{p.fix_available ? 'Yes' : 'No'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

export default function PlatformUpdateCheckPage() {
  const showError = useErrorToast();
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(() => {
    axios.get('/api/platform-update-check')
      .then((res) => setData(res.data.data))
      .catch((err) => showError(err, "Couldn't load the Platform Update Check"));
  }, [showError]);

  useEffect(() => { load(); }, [load]);

  const checkNow = () => {
    setChecking(true);
    axios.post('/api/platform-update-check/check-now')
      .then((res) => setData(res.data.data))
      .catch((err) => showError(err, "Couldn't run the Platform Update Check"))
      .finally(() => setChecking(false));
  };

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Platform Update Check</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            onClick={checkNow}
            disabled={checking}
            title="Check the Node.js release list and the API's packages now"
            style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: checking ? 'default' : 'pointer', fontSize: 13, opacity: checking ? 0.7 : 1 }}
          >
            {checking ? 'Checking…' : 'Check Now'}
          </button>
        </div>
      </div>
      <div style={{ padding: '16px 24px 0' }}>
        {data ? (
          <>
            <NodeSection node={data.node} />
            <PackagesSection packages={data.packages} />
          </>
        ) : (
          <div style={MUTED}>Loading…</div>
        )}
      </div>
    </div>
  );
}
